import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppContext } from '../../../app/Context';
import { getSsoStatus, ssoLogin } from '../../../db/auth';
import { NextScreen } from '../../../db/auth/types';
import routes from '../../../routes/router';
import { useInterval } from '../../../utils/timer';
import { useAuthContext } from '../Context';
import { AuthScreen } from '../types';
import { setUserId } from '../../../db/session';
import { getLocalData } from '../../../db/local-storage';
import { getUrl } from '../../../db/shared';
import Logger from '../../../utils/logger';

const useSsoVM = () => {
  const navigate = useNavigate();

  const { presentationId, activeSlideId, setSession, ssoCredentials, setSsoCredentials } = useAppContext();
  const { setScreen } = useAuthContext();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { start, stop } = useInterval(async () => {
    if (!ssoCredentials?.sessionId) {
      stopSso();
      return;
    }

    try {
      const response = await getSsoStatus({ sessionId: ssoCredentials.sessionId });

      switch (response.message) {
        case 'success':
          stopSso();

          if (!presentationId) return;
          if (!activeSlideId) return;

          const { cluster } = response;
          const { next, session } = await ssoLogin({
            baseURL: cluster,
            sessionId: ssoCredentials.sessionId,
          });

          await getLocalData({ presentationId }, session.token);

          setSession(session);
          setUserId(session.userId);

          switch (next) {
            case NextScreen.LANDING_PAGE:
              const url = getUrl(presentationId, activeSlideId) ?? routes.home.url();
              navigate(url, { replace: true });

              break;
            default:
              break;
          }

          break;
        case 'failed':
          stopSso();

          const { errorMessage } = response;
          setErrorMessage(errorMessage);
          break;
        case 'waiting':
        default:
          // do nothing, just wait for next call
          break;
      }
    } catch (error: any) {
      Logger.error(error);
      stopSso();
      setErrorMessage(error.message ?? 'Unknown error');
    }
  }, 5000);

  const stopSso = useCallback(() => {
    stop();
    setSsoCredentials(null);
  }, [setSsoCredentials, stop]);

  useEffect(
    function () {
      if (!ssoCredentials?.authUrl) return;
      window.open(ssoCredentials.authUrl, '_blank');
      start();
    },
    [ssoCredentials?.authUrl, start]
  );

  const handleLoginAgain = () => {
    setScreen(AuthScreen.LOGIN);
  };

  return { errorMessage, handleLoginAgain };
};

export default useSsoVM;
